import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useQueries, useQuery } from 'react-query';

import Checkbox from 'src/components/Checkbox';
import { getGroupMembers } from 'src/api/functions/userApi';
import { getUser } from 'src/api/functions/userManagementApi';
import { queryNames } from 'src/api/utils/queryNames';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { GroupMember } from 'src/types/TUser';

import styles from './styles.module.scss';

/** Up to this many people the account is listed with checkboxes; above it, a search picker. */
export const EXEMPT_LIST_MAX_USERS = 5;

const SEARCH_MIN_CHARS = 2;

/** Dev builds only: `?simulate_large_account=1` shows the search picker however
 *  few people the local account has, so it can be tried by hand. It searches
 *  the real directory; nothing is faked on save. */
const simulateLargeAccount = () =>
  process.env.NODE_ENV === 'development' &&
  new URLSearchParams(window.location.search).get('simulate_large_account') === '1';
const SEARCH_DEBOUNCE_MS = 250;

const displayName = (m: { first_name?: string; last_name?: string; email?: string }) =>
  [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email || '';

type ExemptUsersPickerProps = {
  selectedIds: number[];
  onChange: (ids: number[]) => void;
};

/**
 * "Can replace without approval": who may replace assets without their new
 * file waiting for release. Small accounts get a checkbox per person; larger
 * ones get a search box (name or email) and the chosen people as removable
 * chips, so the page never renders hundreds of rows.
 */
export const ExemptUsersPicker: React.FC<ExemptUsersPickerProps> = ({ selectedIds, onChange }) => {
  const accessToken = useAccessToken();

  // One page of the directory: tells us how big the account is and names
  // most selected people without a lookup each.
  const { data: firstPage, isLoading } = useQuery(
    [queryNames.groupMembers, '__exempt_first_page__'],
    () => getGroupMembers(accessToken, undefined, 200),
    { staleTime: 1000 * 60 * 5 },
  );
  const total: number = (firstPage as any)?.data?.total_users ?? 0;
  const firstMembers: GroupMember[] = (firstPage as any)?.data?.members ?? [];
  const simulated = simulateLargeAccount();
  const useSearch = simulated || total > EXEMPT_LIST_MAX_USERS;

  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [term]);

  const { data: searchResponse, isFetching: searching } = useQuery(
    [queryNames.groupMembers, '__exempt_search__', debounced],
    () => getGroupMembers(accessToken, debounced, 10),
    { enabled: useSearch && debounced.length >= SEARCH_MIN_CHARS, keepPreviousData: true, staleTime: 30_000 },
  );
  const results: GroupMember[] = (searchResponse as any)?.data?.members ?? [];

  // names for selected people: the first page, people picked from search, and
  // a per-user lookup for anyone else (large accounts)
  const [picked, setPicked] = useState<Record<number, GroupMember>>({});
  const known = useMemo(() => {
    const map: Record<number, GroupMember> = {};
    firstMembers.forEach((m) => {
      map[m.id] = m;
    });
    return { ...map, ...picked };
  }, [firstMembers, picked]);
  const unknownIds = useSearch && !isLoading ? selectedIds.filter((id) => !known[id]) : [];
  const lookups = useQueries(
    unknownIds.map((id) => ({
      queryKey: [queryNames.user, id],
      queryFn: () => getUser(id, accessToken),
      staleTime: 1000 * 60 * 5,
    })),
  );
  const personOf = (id: number): { name: string; email?: string } => {
    if (known[id]) return { name: displayName(known[id]), email: known[id].email };
    const looked = lookups[unknownIds.indexOf(id)]?.data as any;
    const user = looked?.data ?? looked;
    return user?.email ? { name: displayName(user), email: user.email } : { name: `User ${id}` };
  };

  const add = (member: GroupMember) => {
    setPicked((p) => ({ ...p, [member.id]: member }));
    if (!selectedIds.includes(member.id)) onChange([...selectedIds, member.id]);
    setTerm('');
    setDebounced('');
  };
  const remove = (id: number) => onChange(selectedIds.filter((x) => x !== id));

  if (isLoading) {
    return <p className={styles.description}>Loading people…</p>;
  }

  if (!useSearch) {
    return (
      <div className={styles.exemptions} data-test="versioning-exemptions">
        <div className={styles.subheading}>Can replace without approval</div>
        {firstMembers.map((m) => (
          <Checkbox
            key={m.id}
            label={`${displayName(m)} (${m.email})`}
            checked={selectedIds.includes(m.id)}
            onChange={(e) => onChange(e.target.checked ? [...selectedIds, m.id] : selectedIds.filter((x) => x !== m.id))}
          />
        ))}
      </div>
    );
  }

  const showResults = debounced.length >= SEARCH_MIN_CHARS;
  const available = results.filter((m) => !selectedIds.includes(m.id));

  return (
    <div className={styles.exemptions} data-test="versioning-exemptions-search">
      <div className={styles.subheading}>Can replace without approval</div>
      {simulated && (
        <p className={styles.description} data-test="exempt-simulated">
          Dev only: showing the large-account picker for {total} people (simulate_large_account=1).
        </p>
      )}

      {selectedIds.length === 0 ? (
        <p className={styles.description}>Nobody yet — everyone's replacements wait for release.</p>
      ) : (
        <div className={styles.chips}>
          {selectedIds.map((id) => {
            const person = personOf(id);
            return (
              <span key={id} className={styles.chip} data-test={`exempt-chip-${id}`}>
                {person.name}
                {person.email && person.email !== person.name && (
                  <span className={styles.chipEmail}>{person.email}</span>
                )}
                <button type="button" aria-label={`Remove ${person.name}`} onClick={() => remove(id)}>
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}

      <div className={styles.searchBox}>
        <input
          className={styles.searchInput}
          type="search"
          placeholder={`Search ${total} people by name or email`}
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          data-test="exempt-search"
        />
        {showResults && (
          <ul className={styles.results} role="listbox">
            {searching && available.length === 0 && <li className={styles.resultHint}>Searching…</li>}
            {!searching && available.length === 0 && <li className={styles.resultHint}>No one else matches</li>}
            {available.map((m) => (
              <li key={m.id}>
                <button type="button" onClick={() => add(m)} data-test={`exempt-result-${m.id}`}>
                  <span>{displayName(m)}</span>
                  <span className={styles.resultEmail}>{m.email}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ExemptUsersPicker;
